<template>
  <div
    class="drp"
    role="dialog"
    aria-label="Choisir une période"
    @keydown.esc.stop="$emit('close')"
  >
    <!-- Presets (one click = applied) -->
    <aside class="drp-presets" aria-label="Raccourcis">
      <span class="drp-caption">Raccourcis</span>
      <button
        v-for="p in presets"
        :key="p.key"
        type="button"
        class="drp-preset"
        :class="{ active: activePreset === p.key }"
        @click="applyPreset(p.key)"
      >
        <span>{{ p.label }}</span>
        <small v-if="p.hint">{{ p.hint }}</small>
      </button>
    </aside>

    <section class="drp-main">
      <!-- Which date the range applies to -->
      <div v-if="dateTypes.length" class="drp-type">
        <span class="drp-caption">Filtrer sur</span>
        <div class="drp-type-list" role="radiogroup" aria-label="Type de date">
          <button
            v-for="t in dateTypes"
            :key="t.value"
            type="button"
            role="radio"
            class="drp-type-btn"
            :aria-checked="draftType === t.value"
            :class="{ active: draftType === t.value }"
            @click="draftType = t.value"
          >
            {{ t.label }}
          </button>
        </div>
      </div>

      <!-- Editable inputs -->
      <div class="drp-inputs">
        <label class="drp-field" :class="{ focus: !draftStart || (draftStart && draftEnd) }">
          <span>Du</span>
          <input type="date" :value="toInput(draftStart)" @change="onInput('start', $event)" />
        </label>
        <i class="ph ph-arrow-right drp-arrow" aria-hidden="true"></i>
        <label class="drp-field" :class="{ focus: draftStart && !draftEnd }">
          <span>Au</span>
          <input type="date" :value="toInput(draftEnd)" :min="toInput(draftStart)" @change="onInput('end', $event)" />
        </label>
      </div>

      <!-- Calendars -->
      <div class="drp-cals">
        <div v-for="(month, mi) in months" :key="month.key" class="drp-cal">
          <header class="drp-cal-head">
            <button
              v-if="mi === 0"
              type="button"
              class="drp-nav"
              aria-label="Mois précédent"
              @click="shiftMonth(-1)"
            >
              <i class="ph ph-caret-left" aria-hidden="true"></i>
            </button>
            <span v-else class="drp-nav-spacer"></span>
            <strong>{{ month.title }}</strong>
            <button
              v-if="mi === months.length - 1"
              type="button"
              class="drp-nav"
              aria-label="Mois suivant"
              @click="shiftMonth(1)"
            >
              <i class="ph ph-caret-right" aria-hidden="true"></i>
            </button>
            <span v-else class="drp-nav-spacer"></span>
          </header>
          <div class="drp-grid drp-weekdays" aria-hidden="true">
            <span v-for="d in weekdays" :key="d">{{ d }}</span>
          </div>
          <div class="drp-grid" @mouseleave="hover = null">
            <span v-for="n in month.offset" :key="'e' + n" class="drp-empty"></span>
            <button
              v-for="day in month.days"
              :key="day.key"
              type="button"
              class="drp-day"
              :class="dayClass(day.date)"
              :aria-label="day.label"
              :aria-pressed="isEdge(day.date)"
              @click="pickDay(day.date)"
              @mouseenter="hover = day.date"
            >
              {{ day.date.getDate() }}
            </button>
          </div>
        </div>
      </div>

      <!-- Summary + actions -->
      <footer class="drp-foot">
        <p class="drp-summary">
          <template v-if="draftStart">
            <b>{{ fmtLong(draftStart) }}</b>
            <template v-if="draftEnd && !sameDay(draftStart, draftEnd)">
              → <b>{{ fmtLong(draftEnd) }}</b>
            </template>
            <span class="drp-days">{{ dayCount }} jour{{ dayCount > 1 ? "s" : "" }}</span>
          </template>
          <template v-else>Choisissez une date de début</template>
        </p>
        <div class="drp-actions">
          <button type="button" class="btn btn-sm btn-light" @click="$emit('close')">Annuler</button>
          <button type="button" class="btn btn-sm btn-primary" :disabled="!draftStart" @click="applyCustom">
            Appliquer
          </button>
        </div>
      </footer>
    </section>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, onMounted, onBeforeUnmount, PropType, ref } from "vue";
import moment from "moment";

type Range = [moment.Moment, moment.Moment] | null;

const DAY = 86400000;
const startOfDay = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate());
const addDays = (d: Date, n: number) => new Date(d.getFullYear(), d.getMonth(), d.getDate() + n);

/**
 * Date range picker in a popover: presets (applied on click), editable
 * "Du / Au" inputs and 1–2 month calendars with hover preview.
 * Emits `apply` with { range: [moment, moment] | null, preset, dateType }.
 */
export default defineComponent({
  name: "DateRangePopover",
  props: {
    modelValue: { type: Array as unknown as PropType<Range>, default: null },
    preset: { type: String, default: "" },
    dateType: { type: String, default: "" },
    dateTypes: { type: Array as PropType<{ value: string; label: string }[]>, default: () => [] },
  },
  emits: ["apply", "close"],
  setup(props, { emit }) {
    const today = startOfDay(new Date());

    const draftStart = ref<Date | null>(props.modelValue?.[0] ? startOfDay(props.modelValue[0].toDate()) : null);
    const draftEnd = ref<Date | null>(props.modelValue?.[1] ? startOfDay(props.modelValue[1].toDate()) : null);
    const draftType = ref(props.dateType || props.dateTypes[0]?.value || "");
    const activePreset = ref(props.modelValue ? props.preset : "");
    const hover = ref<Date | null>(null);

    // First visible month: the one before the selected end (or today) so the
    // range is visible on the two calendars.
    const anchor = draftEnd.value || draftStart.value || today;
    const view = ref(new Date(anchor.getFullYear(), anchor.getMonth() - 1, 1));

    const isWide = ref(window.matchMedia?.("(min-width: 768px)").matches ?? true);
    const onResize = () => (isWide.value = window.matchMedia?.("(min-width: 768px)").matches ?? true);
    onMounted(() => window.addEventListener("resize", onResize));
    onBeforeUnmount(() => window.removeEventListener("resize", onResize));

    const monthFmt = new Intl.DateTimeFormat("fr-FR", { month: "long", year: "numeric" });
    const longFmt = new Intl.DateTimeFormat("fr-FR", { day: "numeric", month: "short", year: "numeric" });
    const labelFmt = new Intl.DateTimeFormat("fr-FR", { weekday: "long", day: "numeric", month: "long", year: "numeric" });
    const weekdays = ["Lu", "Ma", "Me", "Je", "Ve", "Sa", "Di"];

    const months = computed(() => {
      const count = isWide.value ? 2 : 1;
      // one calendar on mobile: show the month of the anchor, not the one before
      const base = isWide.value ? view.value : new Date(view.value.getFullYear(), view.value.getMonth() + 1, 1);
      return Array.from({ length: count }, (_, i) => {
        const first = new Date(base.getFullYear(), base.getMonth() + i, 1);
        const total = new Date(first.getFullYear(), first.getMonth() + 1, 0).getDate();
        const title = monthFmt.format(first);
        return {
          key: `${first.getFullYear()}-${first.getMonth()}`,
          title: title.charAt(0).toUpperCase() + title.slice(1),
          offset: (first.getDay() + 6) % 7, // Monday first
          days: Array.from({ length: total }, (_, d) => {
            const date = new Date(first.getFullYear(), first.getMonth(), d + 1);
            return { key: date.getTime(), date, label: labelFmt.format(date) };
          }),
        };
      });
    });

    const shiftMonth = (n: number) => {
      view.value = new Date(view.value.getFullYear(), view.value.getMonth() + n, 1);
    };

    const sameDay = (a: Date | null, b: Date | null) => !!a && !!b && a.getTime() === b.getTime();
    const isEdge = (d: Date) => sameDay(d, draftStart.value) || sameDay(d, draftEnd.value);

    // Effective end for highlighting: hover preview while choosing the end
    const previewEnd = computed(() => {
      if (draftStart.value && !draftEnd.value && hover.value && hover.value >= draftStart.value) return hover.value;
      return draftEnd.value;
    });

    function dayClass(d: Date) {
      const s = draftStart.value;
      const e = previewEnd.value;
      return {
        today: sameDay(d, today),
        start: sameDay(d, s),
        end: !!e && sameDay(d, e),
        "in-range": !!s && !!e && d > s && d < e,
        preview: !draftEnd.value && !!e && d > (s as Date) && d <= e,
        weekend: d.getDay() === 0 || d.getDay() === 6,
      };
    }

    function pickDay(d: Date) {
      activePreset.value = "";
      if (!draftStart.value || draftEnd.value) {
        draftStart.value = d;
        draftEnd.value = null;
      } else if (d < draftStart.value) {
        draftStart.value = d;
      } else {
        draftEnd.value = d;
      }
    }

    const toInput = (d: Date | null) =>
      d ? `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}` : "";

    function onInput(which: "start" | "end", event: Event) {
      const value = (event.target as HTMLInputElement).value;
      activePreset.value = "";
      if (!value) {
        if (which === "start") draftStart.value = null;
        else draftEnd.value = null;
        return;
      }
      const [y, m, d] = value.split("-").map(Number);
      const date = new Date(y, m - 1, d);
      if (which === "start") {
        draftStart.value = date;
        if (draftEnd.value && draftEnd.value < date) draftEnd.value = null;
      } else {
        if (draftStart.value && date < draftStart.value) return;
        draftEnd.value = date;
      }
      // `view` is the month before the first displayed on mobile / the first on
      // desktop: this keeps the typed date visible in both layouts.
      view.value = new Date(date.getFullYear(), date.getMonth() - 1, 1);
    }

    const dayCount = computed(() => {
      if (!draftStart.value) return 0;
      const end = draftEnd.value || draftStart.value;
      return Math.round((end.getTime() - draftStart.value.getTime()) / DAY) + 1;
    });

    const fmtLong = (d: Date) => longFmt.format(d);

    // ── Presets ──
    const presets = [
      { key: "", label: "Toutes les dates" },
      { key: "today", label: "Aujourd'hui" },
      { key: "yesterday", label: "Hier" },
      { key: "last7", label: "7 derniers jours" },
      { key: "last30", label: "30 derniers jours" },
      { key: "week", label: "Cette semaine" },
      { key: "month", label: "Ce mois" },
      { key: "lastMonth", label: "Mois dernier" },
      { key: "year", label: "Cette année" },
    ];

    function presetRange(key: string): [Date, Date] | null {
      switch (key) {
        case "today":
          return [today, today];
        case "yesterday":
          return [addDays(today, -1), addDays(today, -1)];
        case "last7":
          return [addDays(today, -6), today];
        case "last30":
          return [addDays(today, -29), today];
        case "week":
          return [addDays(today, -((today.getDay() + 6) % 7)), today];
        case "month":
          return [new Date(today.getFullYear(), today.getMonth(), 1), today];
        case "lastMonth":
          return [
            new Date(today.getFullYear(), today.getMonth() - 1, 1),
            new Date(today.getFullYear(), today.getMonth(), 0),
          ];
        case "year":
          return [new Date(today.getFullYear(), 0, 1), today];
        default:
          return null;
      }
    }

    const toMoment = (r: [Date, Date] | null): Range => (r ? [moment(r[0]), moment(r[1])] : null);

    function applyPreset(key: string) {
      const r = presetRange(key);
      activePreset.value = key;
      draftStart.value = r ? r[0] : null;
      draftEnd.value = r ? r[1] : null;
      emit("apply", { range: toMoment(r), preset: key, dateType: draftType.value });
    }

    function applyCustom() {
      if (!draftStart.value) return;
      const end = draftEnd.value || draftStart.value;
      emit("apply", { range: toMoment([draftStart.value, end]), preset: "custom", dateType: draftType.value });
    }

    return {
      presets,
      activePreset,
      draftStart,
      draftEnd,
      draftType,
      hover,
      months,
      weekdays,
      shiftMonth,
      dayClass,
      isEdge,
      pickDay,
      toInput,
      onInput,
      dayCount,
      fmtLong,
      sameDay,
      applyPreset,
      applyCustom,
    };
  },
});
</script>

<style scoped>
.drp {
  position: absolute;
  z-index: 1060;
  top: calc(100% + 10px);
  left: 0;
  display: flex;
  width: max-content;
  max-width: calc(100vw - 32px);
  border: 1px solid var(--ui-border);
  border-radius: var(--ui-radius-lg);
  background: var(--ui-surface-elevated);
  box-shadow: var(--ui-shadow-pop);
  overflow: hidden;
  animation: drp-in var(--ui-dur) var(--ui-ease-spring);
}
@keyframes drp-in {
  from { opacity: 0; transform: translateY(-6px) scale(0.98); }
  to { opacity: 1; transform: none; }
}
.drp-caption {
  display: block;
  margin-bottom: 8px;
  color: var(--ui-text-muted);
  font-size: var(--ui-fs-micro);
  font-weight: var(--ui-fw-black);
  letter-spacing: var(--ui-tracking-label);
  text-transform: uppercase;
}

/* Presets column */
.drp-presets {
  display: flex;
  flex-direction: column;
  gap: 2px;
  width: 190px;
  padding: 16px 10px;
  border-right: 1px solid var(--ui-border);
  background: var(--ui-surface-sunken);
}
.drp-presets .drp-caption {
  padding: 0 8px;
}
.drp-preset {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: 9px 12px;
  border: 0;
  border-radius: var(--ui-radius-sm);
  background: transparent;
  color: var(--ui-text-secondary);
  font-size: var(--ui-fs-sm);
  font-weight: var(--ui-fw-semibold);
  text-align: left;
  transition: background-color var(--ui-dur-fast) var(--ui-ease), color var(--ui-dur-fast) var(--ui-ease);
}
.drp-preset:hover {
  background: var(--ui-surface);
  color: var(--ui-text);
}
.drp-preset.active {
  background: var(--ui-accent);
  color: #FFFFFF;
  box-shadow: 0 4px 12px rgba(var(--ui-accent-rgb), 0.28);
}

/* Main column */
.drp-main {
  display: flex;
  flex-direction: column;
  gap: 14px;
  padding: 16px 18px;
}
.drp-type-list {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}
.drp-type-btn {
  padding: 6px 12px;
  border: 1px solid var(--ui-border);
  border-radius: var(--ui-radius-pill);
  background: var(--ui-surface);
  color: var(--ui-text-secondary);
  font-size: var(--ui-fs-xs);
  font-weight: var(--ui-fw-bold);
  transition: all var(--ui-dur-fast) var(--ui-ease);
}
.drp-type-btn:hover {
  border-color: var(--ui-accent);
  color: var(--ui-accent-text);
}
.drp-type-btn.active {
  border-color: var(--ui-accent);
  background: var(--ui-accent-soft);
  color: var(--ui-accent-text);
}

.drp-inputs {
  display: flex;
  align-items: center;
  gap: 10px;
}
.drp-field {
  flex: 1 1 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 7px 12px;
  border: 1px solid var(--ui-border);
  border-radius: var(--ui-radius-md);
  background: var(--ui-surface-sunken);
  transition: border-color var(--ui-dur-fast) var(--ui-ease), box-shadow var(--ui-dur-fast) var(--ui-ease);
}
.drp-field.focus {
  border-color: var(--ui-accent);
  box-shadow: var(--ui-focus-ring);
  background: var(--ui-surface);
}
.drp-field span {
  color: var(--ui-text-muted);
  font-size: var(--ui-fs-micro);
  font-weight: var(--ui-fw-black);
  text-transform: uppercase;
  letter-spacing: var(--ui-tracking-label);
}
.drp-field input {
  width: 100%;
  padding: 0;
  border: 0;
  background: transparent;
  color: var(--ui-text);
  font-size: var(--ui-fs-sm);
  font-weight: var(--ui-fw-bold);
  outline: none;
}
.drp-arrow {
  color: var(--ui-text-faint);
}

.drp-cals {
  display: flex;
  gap: 24px;
}
.drp-cal {
  width: 252px;
}
.drp-cal-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
}
.drp-cal-head strong {
  color: var(--ui-text);
  font-size: var(--ui-fs-sm);
  font-weight: var(--ui-fw-black);
}
.drp-nav,
.drp-nav-spacer {
  width: 32px;
  height: 32px;
}
.drp-nav {
  display: grid;
  place-items: center;
  padding: 0;
  border: 1px solid var(--ui-border);
  border-radius: 50%;
  background: var(--ui-surface);
  color: var(--ui-text);
  transition: background-color var(--ui-dur-fast) var(--ui-ease), border-color var(--ui-dur-fast) var(--ui-ease);
}
.drp-nav:hover {
  border-color: var(--ui-accent);
  background: var(--ui-accent-soft);
  color: var(--ui-accent-text);
}
.drp-grid {
  display: grid;
  grid-template-columns: repeat(7, 36px);
  row-gap: 2px;
}
.drp-weekdays span {
  padding: 4px 0;
  color: var(--ui-text-muted);
  font-size: var(--ui-fs-micro);
  font-weight: var(--ui-fw-bold);
  text-align: center;
}
.drp-day {
  position: relative;
  height: 36px;
  padding: 0;
  border: 0;
  background: transparent;
  color: var(--ui-text);
  font-size: var(--ui-fs-sm);
  font-weight: var(--ui-fw-semibold);
  font-variant-numeric: tabular-nums;
}
.drp-day::before {
  content: "";
  position: absolute;
  inset: 2px;
  border-radius: 50%;
  transition: background-color var(--ui-dur-fast) var(--ui-ease);
  z-index: 0;
}
.drp-day:hover::before {
  background: var(--ui-surface-hover);
}
.drp-day.weekend {
  color: var(--ui-text-muted);
}
.drp-day.today {
  color: var(--ui-accent-text);
  font-weight: var(--ui-fw-black);
}
.drp-day.today::after {
  content: "";
  position: absolute;
  left: 50%;
  bottom: 5px;
  width: 4px;
  height: 4px;
  margin-left: -2px;
  border-radius: 50%;
  background: currentColor;
}
.drp-day.in-range,
.drp-day.preview {
  background: var(--ui-accent-soft);
}
.drp-day.preview {
  background: rgba(var(--ui-accent-rgb), 0.07);
}
.drp-day.start,
.drp-day.end {
  color: #FFFFFF;
}
.drp-day.start::before,
.drp-day.end::before {
  background: var(--ui-accent);
  box-shadow: 0 4px 10px rgba(var(--ui-accent-rgb), 0.35);
}
.drp-day.start:not(.end) {
  background: linear-gradient(90deg, transparent 50%, var(--ui-accent-soft) 50%);
}
.drp-day.end:not(.start) {
  background: linear-gradient(90deg, var(--ui-accent-soft) 50%, transparent 50%);
}
.drp-day > * {
  position: relative;
}
.drp-day {
  isolation: isolate;
}
.drp-day::before {
  z-index: -1;
}

.drp-foot {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding-top: 12px;
  border-top: 1px solid var(--ui-border);
}
.drp-summary {
  margin: 0;
  color: var(--ui-text-secondary);
  font-size: var(--ui-fs-sm);
}
.drp-summary b {
  color: var(--ui-text);
}
.drp-days {
  margin-left: 8px;
  padding: 2px 8px;
  border-radius: var(--ui-radius-pill);
  background: var(--ui-accent-soft);
  color: var(--ui-accent-text);
  font-size: var(--ui-fs-micro);
  font-weight: var(--ui-fw-black);
}
.drp-actions {
  display: flex;
  gap: 8px;
}

/* Mobile: bottom sheet */
@media (max-width: 767.98px) {
  .drp {
    position: fixed;
    top: auto;
    left: 8px;
    right: 8px;
    bottom: 8px;
    flex-direction: column;
    width: auto;
    max-width: none;
    max-height: calc(100vh - 80px);
    overflow-y: auto;
  }
  .drp-presets {
    flex-direction: row;
    width: auto;
    padding: 12px;
    border-right: 0;
    border-bottom: 1px solid var(--ui-border);
    overflow-x: auto;
  }
  .drp-presets .drp-caption {
    display: none;
  }
  .drp-preset {
    flex: 0 0 auto;
    width: auto;
    border: 1px solid var(--ui-border);
    border-radius: var(--ui-radius-pill);
    background: var(--ui-surface);
  }
  .drp-cal {
    width: 100%;
  }
  .drp-grid {
    grid-template-columns: repeat(7, minmax(0, 1fr));
  }
  .drp-foot {
    flex-direction: column;
    align-items: stretch;
  }
  .drp-actions .btn {
    flex: 1 1 0;
  }
}
@media (prefers-reduced-motion: reduce) {
  .drp { animation: none; }
}
</style>
